'use client';
import React, { useContext } from 'react';
import Link from 'next/link';
import { Typography } from '@mui/material';
import { headerLinks } from '@/shared/utils';
import { AuthContext } from '@/auth';
import { TrustpilotWidget } from 'src/modules/page/ui/elements/trustpilot-widget';
import { IUBENDA_INTERNAL_PAGES } from 'src/shared/utils/iubenda-urls';

interface FooterProps {
  description?: string;
}

const DEFAULT_FOOTER_TEXT =
  'Impara a suonare da zero o migliora le tue abilità con i più grandi musicisti italiani.';

const SOCIAL_LINKS: Record<string, { label: string; imgSrc: string; href: string }> = {
  instagram: {
    label: 'Instagram',
    imgSrc: '/assets/images/footer/social/instagram.svg',
    href: 'https://www.instagram.com/vibly.tv/',
  },
  facebook: {
    label: 'Facebook',
    imgSrc: '/assets/images/footer/social/facebook.svg',
    href: 'https://www.facebook.com/vibly.tv/',
  },
  youtube: {
    label: 'Youtube',
    imgSrc: '/assets/images/footer/social/youtube.svg',
    href: 'https://www.youtube.com/channel/UCiOaVD0Y78aLUolyGolbSwA?cbrd=1',
  },
  tiktok: {
    label: 'TikTok',
    imgSrc: '/assets/images/footer/social/tiktok.svg',
    href: 'https://www.tiktok.com/@vibly.tv',
  },
};

const Footer: React.FC<FooterProps> = ({ description = DEFAULT_FOOTER_TEXT }) => {
  const { user, profile } = useContext(AuthContext);
  const links = headerLinks(user !== null, profile?.subscriber);

  return (
    <footer className="px-4 md:px-12 py-16 lg:p-20 bg-paper">
      <div className="max-w-[1440px] m-auto">
        <div className="flex flex-wrap items-start justify-center w-full gap-x-14 gap-y-12 lg:justify-start">
          {/* Company Info Section */}
          <div className="flex flex-col items-center w-full lg:w-auto lg:flex-1 lg:items-start gap-y-6">
            <div className="flex flex-col max-w-full w-[400px]">
              <img
                loading="lazy"
                src="/assets/images/logotipo-white.svg"
                alt="Company logo"
                className="h-14 h-20 w-auto lg:mr-auto"
              />
              <Typography variant="s1" className="mt-6">
                {description}
              </Typography>
            </div>

            <div className="max-w-[400px] w-full">
              <TrustpilotWidget
                isFooter
                containerClassName="flex justify-start"
                widgetClassName="w-[246px] -ml-1"
              />
            </div>
          </div>

          <div className="flex flex-wrap w-full sm:flex-nowrap gap-x-14 gap-y-12 lg:flex-1 max-w-[400px] md:justify-center justify-start lg:justify-start">
            {/* Sitemap Section */}
            <nav className="flex flex-col w-full lg:max-w-[224px]">
              <Typography variant="h4">Sitemap</Typography>
              <ul className="flex flex-col self-start mt-8 space-y-4">
                {links.map((el) => (
                  <li key={el.label}>
                    <Link href={el.href} className="hover:underline">
                      <Typography variant="button-l">{el.label}</Typography>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Social Links Section */}
            <div className="flex flex-col w-full max-w-[400px] lg:max-w-[224px]">
              <Typography variant="h4">Social</Typography>
              <ul className="flex flex-col self-start mt-8 space-y-4">
                {Object.keys(SOCIAL_LINKS).map((social) => {
                  const el = SOCIAL_LINKS[social];
                  return (
                    <li key={social}>
                      <Link
                        href={el.href}
                        className="flex items-center gap-2 hover:underline"
                        target="_blank"
                      >
                        <img
                          loading="lazy"
                          src={el.imgSrc}
                          alt={`${social} icon`}
                          className="w-6 h-auto"
                        />
                        <Typography variant="button-l">{el.label}</Typography>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Payment Methods Section */}
          <div className="flex flex-col w-full max-w-[400px] lg:min-w-[256px]">
            <Typography variant="h4">Metodi pagamento</Typography>
            <div className="flex flex-wrap items-center w-full gap-2 mt-8">
              <img
                loading="lazy"
                src="/assets/images/footer/payment/paypal.svg"
                alt="Payment"
                className="h-[32px] lg:h-[40px] w-auto"
              />
              <img
                loading="lazy"
                src="/assets/images/footer/payment/mastercard.svg"
                alt="Payment"
                className="h-[32px] lg:h-[40px] w-auto"
              />
              <img
                loading="lazy"
                src="/assets/images/footer/payment/visa.svg"
                alt="Payment"
                className="h-[32px] lg:h-[40px] w-auto"
              />
              <img
                loading="lazy"
                src="/assets/images/footer/payment/american-express.svg"
                alt="Payment"
                className="h-[32px] lg:h-[40px] w-auto"
              />
              <img
                loading="lazy"
                src="/assets/images/footer/payment/edenred.svg"
                alt="Payment"
                className="h-[32px] lg:h-[40px] w-auto"
              />
            </div>
          </div>
        </div>

        {/* Footer Bottom Section */}
        <div className="flex flex-col w-full mt-14 md:mt-10">
          <div className="flex flex-wrap items-center justify-center w-full m-auto lg:ml-0 lg:mr-auto gap-x-12 lg:justify-start gap-y-6">
            <Link href="/tocc" className="w-full max-w-[400px]">
              <div className="flex flex-wrap gap-x-12 gap-y-6 sm:min-w-[400px] justify-start">
                <img
                  loading="lazy"
                  src="/assets/images/footer/mic-logo.svg"
                  alt="Ministero della Cultura"
                  className="h-[40px] w-auto"
                />
                <img
                  loading="lazy"
                  src="/assets/images/footer/finanziato-logo.png"
                  alt="Finanziato dall'unione Europea"
                  className="h-[40px] w-auto"
                />
              </div>
            </Link>
          </div>
          <hr className="w-full mt-8 border-action-divider" />
          <div className="flex flex-wrap items-start justify-center w-full gap-12 mt-8 md:justify-between">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 lg:justify-start">
              <Typography
                variant="body-small-500"
                color="textSecondary"
                className="w-full text-center md:w-auto"
              >
                © Vibly, {new Date().getFullYear()}
              </Typography>
              <span className="hidden md:inline-block">|</span>
              <Link
                href={`${process.env.NEXT_PUBLIC_FRONT_END_HOST}/${IUBENDA_INTERNAL_PAGES.PRIVACY_POLICY}`}
                target="_blank"
                rel="noreferrer"
                className="hover:underline text-text-secondary"
              >
                <Typography variant="body-small-500">Privacy policy</Typography>
              </Link>
              <span>|</span>
              <Link
                href={`${process.env.NEXT_PUBLIC_FRONT_END_HOST}/${IUBENDA_INTERNAL_PAGES.PRIVACY_POLICY_COOKIES}`}
                target="_blank"
                rel="noreferrer"
                className="hover:underline text-text-secondary"
              >
                <Typography variant="body-small-500">Cookie policy</Typography>
              </Link>
              <span>|</span>
              <Link
                href={`${process.env.NEXT_PUBLIC_FRONT_END_HOST}/${IUBENDA_INTERNAL_PAGES.TERMS_OF_SERVICE}`}
                target="_blank"
                rel="noreferrer"
                className="hover:underline text-text-secondary"
              >
                <Typography variant="body-small-500">Termini e condizioni</Typography>
              </Link>
            </div>
            <div className="flex gap-2">
              <Typography variant="body-small-500" color="textSecondary">
                Website by
              </Typography>
              <Link href="https://extendi.it" className="hover:underline text-text-secondary">
                <img
                  loading="lazy"
                  src="https://cdn.builder.io/api/v1/image/assets/64022e778ac9432c834a77987bf09f24/0995f68087a17756a1c03c111f7e74eb6dbb3a65ab6fe1833c1913cd381b59c3?apiKey=64022e778ac9432c834a77987bf09f24&"
                  alt="Website creator logo"
                  className="height-[18px] w-auto"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
